import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import { BottomNav } from './components/BottomNav';
import { OfflineBanner } from './components/OfflineBanner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { SubmitPayment } from './pages/SubmitPayment';
import { PaymentHistory } from './pages/PaymentHistory';
import { GroupOverview } from './pages/GroupOverview';
import { Profile } from './pages/Profile';
import { LedgerVerification } from './pages/LedgerVerification';
import { LotteryPortal } from './pages/LotteryPortal';
import { AdminLayout } from './pages/admin/AdminLayout';
import { PendingQueue } from './pages/admin/PendingQueue';
import { ExpenseManagement } from './pages/admin/ExpenseManagement';
import { LandPayments } from './pages/admin/LandPayments';
import { BankReconciliation } from './pages/admin/BankReconciliation';
import { MemberManagement } from './pages/admin/MemberManagement';
import { LegacyImport } from './pages/admin/LegacyImport';
import { AuditLogViewer } from './pages/admin/AuditLogViewer';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-navy-950">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-gray-400 font-medium tracking-wide">FHPF Secure Ledger...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export const App: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-navy-950 text-gray-900 dark:text-gray-100 transition-colors">
      <Navbar />
      <OfflineBanner />

      <main className="flex-1 max-w-5xl w-full mx-auto p-4 sm:p-6">
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submit"
            element={
              <ProtectedRoute>
                <SubmitPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <PaymentHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/group"
            element={
              <ProtectedRoute>
                <GroupOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ledger"
            element={
              <ProtectedRoute>
                <LedgerVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lottery"
            element={
              <ProtectedRoute>
                <LotteryPortal />
              </ProtectedRoute>
            }
          />

          {/* Admin & Treasurer Sub-Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/queue" replace />} />
            <Route path="queue" element={<PendingQueue />} />
            <Route path="expenses" element={<ExpenseManagement />} />
            <Route path="land" element={<LandPayments />} />
            <Route path="reconciliation" element={<BankReconciliation />} />
            <Route path="members" element={<MemberManagement />} />
            <Route path="legacy" element={<LegacyImport />} />
            <Route path="audit" element={<AuditLogViewer />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {user && <BottomNav />}
    </div>
  );
};
